import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { api } from '@/lib/api'
import { AnalyticsService } from '@/services/analytics'

interface UsePaymentsParams {
  userId: string | undefined
  isSignedIn: boolean | undefined
}

interface CheckoutResult {
  checkout_url: string
}

export function usePayments({ userId, isSignedIn }: UsePaymentsParams) {
  const navigate = useNavigate()
  const location = useLocation()

  const [cryptoLoading, setCryptoLoading] = useState<string | null>(null)
  const [telegramLoading, setTelegramLoading] = useState<string | null>(null)

  // Reset loading states when returning to billing page
  useEffect(() => {
    if (location.pathname === '/billing') {
      setCryptoLoading(null)
      setTelegramLoading(null)
    }
  }, [location.pathname])

  // Clear loading when tab becomes visible (user returned from external checkout)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (cryptoLoading) setCryptoLoading(null)
        if (telegramLoading) setTelegramLoading(null)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [cryptoLoading, telegramLoading])

  // Handle success redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const isSuccess = params.get('success') === 'true'

    if (isSuccess && isSignedIn && userId && location.pathname === '/billing') {
      navigate('/billing', { replace: true })
    }
  }, [location.search, location.pathname, isSignedIn, userId, navigate])

  const handleCryptoPurchase = async (planId: string, planName?: string) => {
    if (!userId) return

    if (planName) {
      AnalyticsService.trackBeginCheckout(userId, planId, 0, 'USD')
    }

    setCryptoLoading(planId)
    try {
      const result = await api.post<CheckoutResult>('/payments/nowpayments/create-invoice', {
        user_id: userId,
        plan_id: planId,
      })
      window.location.href = result.checkout_url
    } catch (error) {
      console.error('Failed to create crypto checkout:', error)
      setCryptoLoading(null)
    }
  }

  const handleTelegramPurchase = async (planId: string, planName?: string) => {
    if (!userId) return

    if (planName) {
      AnalyticsService.trackBeginCheckout(userId, planId, 0, 'USD')
    }

    setTelegramLoading(planId)
    try {
      const result = await api.post<CheckoutResult>('/payments/telegram/create-invoice', {
        user_id: userId,
        plan_id: planId,
      })
      window.location.href = result.checkout_url
    } catch (error) {
      console.error('Failed to create Telegram invoice:', error)
      setTelegramLoading(null)
    }
  }

  // Card payments use an on-site inline checkout page (init -> card iframe ->
  // charge), so we navigate there rather than redirecting off-site.
  const handleCardPurchase = (planId: string, planName?: string) => {
    if (!userId) return

    if (planName) {
      AnalyticsService.trackBeginCheckout(userId, planId, 0, 'USD')
    }

    const query = new URLSearchParams({ plan: planId })
    if (planName) query.set('name', planName)
    navigate(`/checkout?${query.toString()}`)
  }

  return {
    cryptoLoading,
    telegramLoading,
    handleCryptoPurchase,
    handleTelegramPurchase,
    handleCardPurchase,
  }
}
